import * as React from "react";
import { FunctionComponent } from "react";
import { useAppDispatch, useAppSelector } from "#store";
import { emptyTableMetaDescriptor, TableMetaDescriptor } from "@inode/types2";
import {
    Button,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    CardHeader,
    Collapse,
    colors,
    Grid,
    IconButton,
    Stack,
    styled,
    Theme,
    Tooltip,
    Typography,
    useTheme,
    Box,
    SxProps,
} from "@mui/material";
import { push } from "redux-first-history";
import { generatePath } from "react-router";
import { Routes } from "#/config";
import { tokenize } from "#/util/stringUtils";
import { ColumnDescriptor, formatNumber } from "@inode/multi-table-explorer";
import { Delete, Download, ExpandMore, Height } from "@mui/icons-material";

import {
    addHistory,
    InteractionType,
    toggleInterpretationFav,
} from "#store/ui";
import { SqlHighlighter } from "#common/SqlHighlighter";
import { IQueryTableResult } from "#services/pilotService/responses/types";
import { Interpretation } from "#services/pilotService/types";
import { Todo } from "#common/Todo";
import {
    deriveValues,
    Options,
    TokenizerOptions,
    WordCloudResponsive,
} from "@inode/wordcloud";
import { Caption, H6, P } from "#/components/Common/headings";
import { ColumnChip } from "#components/Model/TableResult/ColumnChip";
import {
    selectUiHistoryLatestInterpretation,
    selectUiHistoryLatestInterpretationsByDate,
} from "#selectors";
import { isPresent, Optional } from "#types";
import { computeTableDoi } from "#/util/columnSorting";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { scaleLinear } from "d3";
import {
    FavoriteAddIcon,
    FavoriteRemoveIcon,
} from "#components/icons/favorites";
import { generateUrl } from "#components/App/routing/generateUrl";
import { ActionType } from "#services/pilotService/types";
import { Link } from "#common/Link";
import { action } from "@iva/store-utils";
// import { useSnackbar } from 'notistack';
import { ColumnDescriptorExtendedWithScore } from "../interpretation/InterpretationPage";

const HeightIcon = styled(Height)({
    height: "1rem",
    width: "1rem",
});

const WidthIcon = styled(HeightIcon)({
    transform: "rotate(90deg)",
});

const root: SxProps<Theme> = {
    maxWidth: "100%",
    width: "100%",
    minWidth: "100%",
};
const media: SxProps<Theme> = {
    height: 0,
    paddingTop: "56.25%", // 16:9
};
const expand: SxProps<Theme> = {
    transform: "rotate(0deg)",
    marginLeft: "auto",
    transition: theme =>
        theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
};
const expandOpen: SxProps<Theme> = {
    transform: "rotate(180deg)",
    marginLeft: "auto",
    transition: theme =>
        theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
};
const avatar: SxProps<Theme> = {
    backgroundColor: colors.red[500],
};

function getScore(relevancies: number[] | undefined) {
    if (relevancies) {
        return relevancies.reduce((p, c) => p + c, 0);
    } else {
        return 0;
    }
}

type TableCardContentProps = {
    table: IQueryTableResult;
    interpretation: Interpretation;
    expanded?: boolean;
    downloadCsv: (sql: string, dataset: string, rid: string) => void;
};

function ExplanationContainer(props: { interpretationId: number }) {
    return (
        <>
            explanation for {props.interpretationId}
            <Todo>
                Use selector to fetch the operator invocation results of the
                nl2sql ops
            </Todo>
        </>
    );
}

// const TABLE = styled(`table`)(({ theme }) => ({}));
const TD = styled(`td`)(({ theme }) => ({
    textAlign: "center",
    vAlign: "baseline",
    // borderWidth: "2px",
    // borderStyle: "solid",
    // borderColor: theme.palette.success.main,
    padding: 0,
    margin: 0,
}));

function TableGlyph5({ table }: { table: IQueryTableResult }) {
    const theme = useTheme();

    const max = 64;
    const scale = scaleLinear().domain([0, 100]).range([0, max]);
    const fontsize = theme.typography.caption.fontSize;
    const rows = table.data.length;
    const cols = table.columns.length;
    const clipid = `myClip-${table.tableId}`;

    return (
        <svg width={max} height={max}>
            {/*<defs>*/}
            {/*    <marker id="startarrow" markerWidth="10" markerHeight="7"*/}
            {/*            refX="10" refY="3.5" orient="auto">*/}
            {/*        <polygon points="10 0, 10 7, 0 3.5" fill="red" />*/}
            {/*    </marker>*/}
            {/*    <marker id="endarrow" markerWidth="10" markerHeight="7"*/}
            {/*            refX="0" refY="3.5" orient="auto" markerUnits="strokeWidth">*/}
            {/*        <polygon points="0 0, 10 3.5, 0 7" fill="red" />*/}
            {/*    </marker>*/}
            {/*    /!*<filter x="0" y="0" width="1" height="1" id="solid">*!/*/}
            {/*    /!*    <feFlood floodColor={alpha(theme.palette.background.default, 0.1)} result="bg" />*!/*/}
            {/*    /!*    <feMerge>*!/*/}
            {/*    /!*        <feMergeNode in="bg"/>*!/*/}
            {/*    /!*        <feMergeNode in="SourceGraphic"/>*!/*/}
            {/*    /!*    </feMerge>*!/*/}
            {/*    /!*</filter>*!/*/}
            {/*</defs>*/}
            <clipPath id={clipid}>
                {/* Everything outside the circle will be clipped and therefore invisible. */}
                <rect x1={0} width={scale(cols)} y1={0} height={scale(rows)} />
                {/*<rect x1={scale(cols)} width={max} y1={scale(rows)} height={max} />*/}
            </clipPath>

            <rect
                x1={0}
                width={max}
                y1={0}
                height={max}
                fill={theme.palette.background.default}
                stroke={theme.palette.background.paper}
            />
            <text
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize={fontsize}
                fill={theme.palette.text.primary}
                x="50%"
                y="30%"
            >
                {rows} rows
            </text>
            <text
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize={fontsize}
                fill={theme.palette.text.primary}
                x="50%"
                y="50%"
            >
                x
            </text>
            <text
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize={fontsize}
                fill={theme.palette.text.primary}
                x="50%"
                y="70%"
            >
                {cols} cols
            </text>
            <rect
                x1={0}
                width={scale(cols)}
                y1={0}
                height={scale(rows)}
                fill={theme.palette.primary.main}
            />
            <g clipPath={`url(#${clipid})`}>
                <text
                    dominantBaseline="middle"
                    textAnchor="middle"
                    fontSize={fontsize}
                    fill={theme.palette.primary.contrastText}
                    x="50%"
                    y="30%"
                >
                    {rows} rows
                </text>
                <text
                    dominantBaseline="middle"
                    textAnchor="middle"
                    fontSize={fontsize}
                    fill={theme.palette.primary.contrastText}
                    x="50%"
                    y="50%"
                >
                    x
                </text>
                <text
                    dominantBaseline="middle"
                    textAnchor="middle"
                    fontSize={fontsize}
                    fill={theme.palette.primary.contrastText}
                    x="50%"
                    y="70%"
                >
                    {cols} cols
                </text>
            </g>
        </svg>
    );
}

function TableGlyph4({ table }: { table: IQueryTableResult }) {
    const theme = useTheme();
    return (
        <table
            style={{
                textAlign: "center",
                backgroundColor: theme.palette.background.default,
                borderSpacing: "0",
                borderCollapse: "collapse",
            }}
        >
            <tr>
                <TD />
                <TD>
                    {/*<ArrowBackIcon />*/}
                    {table.columns.length}
                    <br />
                    <WidthIcon />
                    {/*<ArrowForwardIcon />*/}
                </TD>
            </tr>
            <tr>
                <TD>
                    {/*<ArrowUpwardIcon />*/}
                    {/*<br />*/}
                    {table.data.length} <HeightIcon />
                    {/*<br />*/}
                    {/*<ArrowDownwardIcon />*/}
                </TD>
                <TD>
                    <CalendarViewMonthIcon sx={{ fontSize: "1rem" }} />
                </TD>
            </tr>
        </table>
    );
}

function TableGlyph3({ table }: { table: IQueryTableResult }) {
    const theme = useTheme();
    return (
        <table
            style={{
                textAlign: "center",
                backgroundColor: theme.palette.background.default,
                borderSpacing: "0",
                borderCollapse: "collapse",
            }}
        >
            <tr>
                <TD />
                <TD>
                    <ArrowBackIcon />
                </TD>
                <TD>{table.columns.length}</TD>
                <TD>
                    <ArrowForwardIcon />
                </TD>
            </tr>
            <tr>
                <TD>
                    <ArrowUpwardIcon />
                </TD>
                <TD rowSpan={3} colSpan={3}>
                    <CalendarViewMonthIcon sx={{ fontSize: "2rem" }} />
                </TD>
            </tr>
            <tr>
                <TD>{table.data.length}</TD>
            </tr>
            <tr>
                <TD>
                    <ArrowDownwardIcon />
                </TD>
            </tr>
        </table>
    );
}

function TableGlyph2({ table }: { table: IQueryTableResult }) {
    const theme = useTheme();
    return (
        <>
            <table
                style={{
                    textAlign: "center",
                    backgroundColor: theme.palette.background.default,
                    borderSpacing: "0",
                    borderCollapse: "collapse",
                }}
            >
                <thead>
                    <tr>
                        <TD>
                            <HeightIcon />
                        </TD>
                        <TD>x</TD>
                        <TD>
                            <WidthIcon />
                        </TD>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <TD>{table.data.length}</TD>
                        <TD>x</TD>
                        <TD>{table.columns.length}</TD>
                    </tr>
                </tbody>
            </table>
            {/*<P><HeightIcon /> {table.data.length} rows <br/>x{" "} <br/> <WidthIcon /> {table.columns.length} cols</P>*/}
        </>
    );
}

function TableGlyph({ table }: { table: IQueryTableResult }) {
    const theme = useTheme();
    return (
        <>
            <table
                style={{
                    textAlign: "center",
                    backgroundColor: theme.palette.background.default,
                    borderSpacing: "0",
                    borderCollapse: "collapse",
                }}
            >
                <thead>
                    <tr>
                        <TD>
                            <HeightIcon />
                        </TD>
                        <TD>x</TD>
                        <TD>
                            <WidthIcon />
                        </TD>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <TD>{table.data.length}</TD>
                        <TD>x</TD>
                        <TD>{table.columns.length}</TD>
                    </tr>
                </tbody>
            </table>
        </>
    );
}

export const TableCardContent: FunctionComponent<TableCardContentProps> = ({
    table,
    interpretation,
    downloadCsv,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const doiWeights = useAppSelector(state => {
        return state.uiState.doiWeights;
    });
    const favoriteColumns = useAppSelector(state => {
        return state.uiState.favoriteColumns;
    });
    const isFav = useAppSelector(state =>
        state.uiState.activeInterpretations.includes(interpretation.id)
    );
    const userHighlight: Interpretation[] = useAppSelector(
        selectUiHistoryLatestInterpretationsByDate
    );
    const datasource = useAppSelector(
        state => state.uiState.selectedDatasource
    ).name;
    const rid = useAppSelector(state => state.uiState.rid);

    const [expanded, setExpanded] = React.useState(false);

    const cols: ColumnDescriptorExtendedWithScore[] = React.useMemo(() => {
        if (table.meta.columns) {
            const doiScores = computeTableDoi(
                table,
                doiWeights,
                undefined,
                undefined,
                favoriteColumns
            );
            return table.meta.columns
                .map((colName, index) => {
                    return {
                        name: colName || "",
                        score: doiScores[index],
                        index: index,
                        descriptor: table.meta.descriptors
                            ? table.meta.descriptors[index]
                            : emptyTableMetaDescriptor,
                    };
                })
                .sort((a, b) => -a.score + b.score)
                .slice(0, Math.min(4, table.meta.columns.length));
        } else {
            return [];
        }
    }, [
        table,
        table.meta.columns,
        table.meta.descriptors,
        doiWeights,
        favoriteColumns,
        userHighlight,
        table.meta.columns,
        table.meta.descriptors,
    ]);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const score = getScore(table.meta.relevancies);
    const isHighlight = !!userHighlight.find(i => i.id === interpretation.id);

    // const {enqueueSnackbar, closeSnackbar}=useSnackbar();
    return (
        <Card
            sx={root}
            // ref={ref}
            raised={isHighlight}
            onMouseOver={() => {
                if (!isHighlight) {
                    dispatch(
                        addHistory(interpretation, InteractionType.HIGHLIGHT)
                    );
                }
            }}
        >
            <CardActionArea
                component="span"
                onClick={() =>
                    dispatch(
                        push(
                            generatePath(Routes.interpretation.path, {
                                id: String(interpretation.id),
                            })
                        )
                    )
                }
            >
                <CardHeader
                    avatar={<TableGlyph5 table={table} />}
                    title={<>{table.caption}</>}
                    subheader={
                        <>
                            Interpretation {interpretation.id} /{" "}
                            {interpretation.dataset} -{" "}
                            {interpretation.operatorBaseType} -{" "}
                            {interpretation.operatorSpecificType}
                            {/*<div style={{fontSize: "0.6rem"}}> <SqlHighlighter code={table.sourceQuery} /></div>*/}
                        </>
                    }
                />
            </CardActionArea>

            <CardContent>
                <Stack direction={"row"}>
                    <Box sx={{ flexGrow: 1 }}>
                        <P>{table.meta?.explanation as string}</P>
                        {table.meta?.quickAnswer ? (
                            <P>
                                <>
                                    <b>Quick answer:</b>
                                    {table.meta?.quickAnswer}
                                </>
                            </P>
                        ) : (
                            cols.map((c, i) => (
                                <ColumnChip
                                    descriptor={c.descriptor}
                                    label={c.name}
                                    key={c.name}
                                    score={c.score}
                                />
                            ))
                        )}
                    </Box>
                    <Box>
                        <Tooltip
                            title={
                                isFav
                                    ? "Remove Interpretation from Library"
                                    : "Add Interpretation to Library"
                            }
                        >
                            <IconButton
                                aria-label="add to favorites"
                                size="large"
                                onClick={() =>
                                    dispatch(
                                        toggleInterpretationFav(
                                            interpretation.id
                                        )
                                    )
                                }
                            >
                                {isFav ? (
                                    <FavoriteRemoveIcon />
                                ) : (
                                    <FavoriteAddIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};
